import { ComponentType, createContext, Dispatch, MutableRefObject } from "react";

import { WindowPosition } from "../../components/window/windowPositioning";

/**
 * Props for all window components (core and third-party).
 * WindowManager provides onClose and position.
 * Focus and z-index stacking are handled by WindowManager's wrapper div —
 * components do not need to accept or forward those concerns.
 */
export interface BaseWindowProps {
    position?: WindowPosition;
    onClose?: () => void;
}

export interface WindowConfig {
    id: string;
    defaultPosition: WindowPosition;
    component: ComponentType<BaseWindowProps>;
}

export interface WindowState {
    open: boolean;
    position: WindowPosition;
    zIndex: number;
}

export interface WindowEntry {
    config: WindowConfig;
    state: WindowState;
}

export const initialState = {} as Record<string, WindowEntry>;

export type State = typeof initialState;

enum ActionTypes {
    AddWindow,
    RemoveWindow,
    OpenWindow,
    CloseWindow,
    ToggleWindow,
    FocusWindow,
}

function addWindow(config: WindowConfig) {
    return {
        type: ActionTypes.AddWindow as const,
        config,
    };
}

function removeWindow(windowId: string) {
    return {
        type: ActionTypes.RemoveWindow as const,
        windowId,
    };
}

function openWindow(windowId: string, position?: WindowPosition) {
    return {
        type: ActionTypes.OpenWindow as const,
        windowId,
        position,
    };
}

function closeWindow(windowId: string) {
    return {
        type: ActionTypes.CloseWindow as const,
        windowId,
    };
}

function toggleWindow(windowId: string, position?: WindowPosition) {
    return {
        type: ActionTypes.ToggleWindow as const,
        windowId,
        position,
    };
}

function focusWindow(windowId: string) {
    return {
        type: ActionTypes.FocusWindow as const,
        windowId,
    };
}

export const actions = { addWindow, removeWindow, openWindow, closeWindow, toggleWindow, focusWindow };

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchWindows = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchWindows>(undefined as any);

/**
 * Helper to renormalize Z-indices.
 * 1. Takes all currently open windows.
 * 2. Sorts them by their current zIndex.
 * 3. Moves the `activeWindowId` to the end of the list.
 * 4. Reassigns zIndex starting from baseZIndex (e.g. 1000).
 */
function reorderWindows(state: State, activeWindowId: string): State {
    const baseZIndex = 1000;

    // Get all window entries
    const entries = Object.entries(state);

    // Separate the active window from others
    const otherWindows = entries.filter(([id]) => id !== activeWindowId);
    const activeWindowEntry = entries.find(([id]) => id === activeWindowId);

    if (!activeWindowEntry) {
        return state;
    }

    // Sort others by their existing zIndex to maintain relative order
    // Default to 0 if zIndex is undefined (though it should be defined)
    otherWindows.sort(([, a], [, b]) => (a.state.zIndex || 0) - (b.state.zIndex || 0));

    // Create new sorted list: [...others, active]
    const sortedEntries = [...otherWindows, activeWindowEntry];

    // Reconstruct state with new z-indices
    const nextState = { ...state };
    sortedEntries.forEach(([id, entry], index) => {
        nextState[id] = {
            ...entry,
            state: {
                ...entry.state,
                zIndex: baseZIndex + index,
            },
        };
    });

    return nextState;
}

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.AddWindow: {
            const windowId = action.config.id;

            if (state[windowId]) {
                return state;
            }

            // Determine initial zIndex (highest + 1)
            const maxZ = Math.max(1000, ...Object.values(state).map((w) => w.state.zIndex || 1000));

            return {
                ...state,
                [windowId]: {
                    config: action.config,
                    state: {
                        open: false,
                        position: action.config.defaultPosition,
                        zIndex: maxZ + 1,
                    },
                },
            };
        }
        case ActionTypes.RemoveWindow: {
            const { [action.windowId]: _removed, ...remaining } = state;
            return remaining;
        }
        case ActionTypes.OpenWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            const intermediateState = {
                ...state,
                [action.windowId]: {
                    ...windowEntry,
                    state: {
                        ...windowEntry.state,
                        open: true,
                        position: action.position ?? windowEntry.config.defaultPosition,
                        zIndex: windowEntry.state.zIndex || 1000,
                    },
                },
            };

            return reorderWindows(intermediateState, action.windowId);
        }
        case ActionTypes.CloseWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            return {
                ...state,
                [action.windowId]: {
                    ...windowEntry,
                    state: {
                        ...windowEntry.state,
                        open: false,
                        position: windowEntry.config.defaultPosition,
                        zIndex: windowEntry.state.zIndex,
                    },
                },
            };
        }
        case ActionTypes.ToggleWindow: {
            const windowEntry = state[action.windowId];

            if (!windowEntry) {
                console.warn(`Window "${action.windowId}" is not registered`);
                return state;
            }

            const willBeOpen = !windowEntry.state.open;

            if (willBeOpen) {
                const intermediateState = {
                    ...state,
                    [action.windowId]: {
                        ...windowEntry,
                        state: {
                            ...windowEntry.state,
                            open: true,
                            position: windowEntry.state.open
                                ? windowEntry.config.defaultPosition
                                : (action.position ?? windowEntry.config.defaultPosition),
                        },
                    },
                };
                return reorderWindows(intermediateState, action.windowId);
            } else {
                return {
                    ...state,
                    [action.windowId]: {
                        ...windowEntry,
                        state: {
                            ...windowEntry.state,
                            open: false,
                            position: windowEntry.state.open
                                ? windowEntry.config.defaultPosition
                                : (action.position ?? windowEntry.config.defaultPosition),
                        },
                    },
                };
            }
        }
        case ActionTypes.FocusWindow: {
            return reorderWindows(state, action.windowId);
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
